import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { treatTableViews } from 'shared/serializer';
import { removeAttributes } from 'shared/utils/view';
import { appActions } from 'store/app/app.actions';
import type { ResourceType } from 'types/resource-types';
import type { TableFilter } from 'types/views';

import { viewToUrlFilters } from '../filters/filterParams';
import { buildViewNavigationUrl, resolveViewToNavigateTo } from '../viewNavigation';

type UseTableViewsOptions = {
  tableName: ResourceType;
  setAvailableFilters: (filters: TableFilter[]) => void;
};

/**
 * Loads the table's view list and available filters. On the initial load and
 * whenever the selected view (URL hash) changes, it fetches the views and
 * navigates to the right one, and fetches the available filters. Returns
 * refreshViews so the caller can re-run both after a view-settings change.
 */
export const useTableViews = ({ tableName, setAvailableFilters }: UseTableViewsOptions) => {
  const history = useHistory();
  const location = useLocation();

  const refreshViews = () => {
    appActions.getViews({
      data: { view_type: tableName },
      callback: ({ data }: any) => {
        const views = treatTableViews(data);
        const viewToNavigateToId = resolveViewToNavigateTo(views, location.hash.replace('#', ''));
        const view = views.find(_view => _view.id === viewToNavigateToId);

        history.push(
          buildViewNavigationUrl({
            pathname: location.pathname,
            search: location.search,
            viewFilters: view ? viewToUrlFilters(view) : {},
            viewId: viewToNavigateToId,
          }),
        );
      },
    });

    appActions.getFilters({
      data: { view: tableName },
      callback: ({ data }: any) => {
        setAvailableFilters(data.map((item: any) => removeAttributes(item)));
      },
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-runs when the selected view (hash) changes; the reads use the current router/props.
  useEffect(refreshViews, [location.hash]);

  return { refreshViews };
};
